/**
 * Registration page for Aegix One.
 *
 * Allows parents/guardians to create an account via email/password
 * or social login. On success, may redirect to email verification.
 */

import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  chakra,
  Flex,
  Text,
  Input,
  InputGroup,
  Image,
  VStack,
  Separator,
  Link,
  IconButton,
} from '@chakra-ui/react';
import { HiEye, HiEyeSlash } from 'react-icons/hi2';
import { Button } from '@themes/aegix';
import { useColorMode } from '@/components/ui/color-mode';
import logoLight from '@/assets/aegix-one-logo-light.svg';
import logoDark from '@/assets/aegix-one-logo-dark.svg';
import { SocialLoginButtons } from '@/components/SocialLoginButtons';
import { registerSchema, type RegisterFormValues } from '@/validation/auth';
import { useRegisterForm } from '@/hooks/useRegisterForm';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function Register() {
  const { t } = useTranslation();
  useDocumentTitle(t('register.createAccount'));
  const { colorMode } = useColorMode();
  const { error, loading, showPassword, setShowPassword, handleSubmit: onSubmit } = useRegisterForm();

  const {
    register: registerField,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  return (
    <Flex
      height="100vh"
      alignItems="center"
      justifyContent="center"
      bg="surface.primary"
      py={8}
    >
      <Box
        maxW="400px"
        w="100%"
        p={8}
        borderWidth="1px"
        borderColor="border.primary"
        borderRadius="lg"
        bg="surface.primary"
        boxShadow="0px 2.5px 4px -1px rgba(0, 0, 0, 0.15)"
      >
        <VStack gap={6}>
          <Image
            src={colorMode === 'dark' ? logoDark : logoLight}
            alt="Aegix One"
            w="200px"
          />

          <Text fontSize="sm" color="text.secondary" textAlign="center">
            {t('register.subtitle')}
          </Text>

          {/* Social signup */}
          <SocialLoginButtons />

          {/* Divider */}
          <Flex align="center" w="100%" gap={3}>
            <Separator flex="1" />
            <Text fontSize="xs" color="text.tertiary" whiteSpace="nowrap">
              {t('register.orEmail')}
            </Text>
            <Separator flex="1" />
          </Flex>

          {/* Registration form */}
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <VStack gap={4} w="100%">
              <Flex gap={3} w="100%">
                <Box flex="1">
                  <chakra.label htmlFor="register-firstName" fontSize="sm" color="text.secondary" mb={1} display="block">
                    {t('register.firstNameLabel')}
                  </chakra.label>
                  <Input
                    id="register-firstName"
                    type="text"
                    autoComplete="given-name"
                    {...registerField('firstName')}
                    placeholder={t('register.firstNamePlaceholder')}
                    size="md"
                  />
                </Box>
                <Box flex="1">
                  <chakra.label htmlFor="register-lastName" fontSize="sm" color="text.secondary" mb={1} display="block">
                    {t('register.lastNameLabel')}
                  </chakra.label>
                  <Input
                    id="register-lastName"
                    type="text"
                    autoComplete="family-name"
                    {...registerField('lastName')}
                    placeholder={t('register.lastNamePlaceholder')}
                    size="md"
                  />
                </Box>
              </Flex>

              <Box w="100%">
                <chakra.label htmlFor="register-email" fontSize="sm" color="text.secondary" mb={1} display="block">
                  {t('register.emailLabel')}
                </chakra.label>
                <Input
                  id="register-email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'register-email-error' : undefined}
                  {...registerField('email')}
                  placeholder={t('register.emailPlaceholder')}
                  size="md"
                />
                {errors.email && (
                  <Text id="register-email-error" role="alert" color="red.500" fontSize="sm">
                    {t(errors.email.message!)}
                  </Text>
                )}
              </Box>

              <Box w="100%">
                <chakra.label htmlFor="register-password" fontSize="sm" color="text.secondary" mb={1} display="block">
                  {t('register.passwordLabel')}
                </chakra.label>
                <InputGroup
                  endElement={
                    <IconButton
                      aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      size="xs"
                      color="text.secondary"
                    >
                      {showPassword ? <HiEyeSlash /> : <HiEye />}
                    </IconButton>
                  }
                >
                  <Input
                    id="register-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    aria-invalid={!!errors.password}
                    aria-describedby={errors.password ? 'register-password-error' : undefined}
                    {...registerField('password')}
                    placeholder={t('register.passwordPlaceholder')}
                    size="md"
                  />
                </InputGroup>
                {errors.password && (
                  <Text id="register-password-error" role="alert" color="red.500" fontSize="sm">
                    {t(errors.password.message!)}
                  </Text>
                )}
              </Box>

              <Box w="100%">
                <chakra.label htmlFor="register-confirmPassword" fontSize="sm" color="text.secondary" mb={1} display="block">
                  {t('register.confirmPasswordLabel')}
                </chakra.label>
                <InputGroup
                  endElement={
                    <IconButton
                      aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      size="xs"
                      color="text.secondary"
                    >
                      {showPassword ? <HiEyeSlash /> : <HiEye />}
                    </IconButton>
                  }
                >
                  <Input
                    id="register-confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    aria-invalid={!!errors.confirmPassword}
                    aria-describedby={errors.confirmPassword ? 'register-confirmPassword-error' : undefined}
                    {...registerField('confirmPassword')}
                    placeholder={t('register.confirmPasswordPlaceholder')}
                    size="md"
                  />
                </InputGroup>
                {errors.confirmPassword && (
                  <Text id="register-confirmPassword-error" role="alert" color="red.500" fontSize="sm">
                    {t(errors.confirmPassword.message!)}
                  </Text>
                )}
              </Box>

              {error && (
                <Text role="alert" aria-live="assertive" color="red.500" fontSize="sm">
                  {error}
                </Text>
              )}

              <Button
                visual="primary"
                type="submit"
                disabled={loading || !isValid}
                style={{ width: '100%' }}
              >
                {loading ? t('register.creatingAccount') : t('register.createAccountBtn')}
              </Button>
            </VStack>
          </form>

          <Text fontSize="sm" color="text.secondary">
            {t('register.alreadyHaveAccount')}{' '}
            <Link asChild color="blue.500" fontWeight="medium">
              <RouterLink to="/login">{t('register.signIn')}</RouterLink>
            </Link>
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
}
